import { useState } from "react";
import { Pause, Play } from "lucide-react";
import type { CedenteDoSistema, Sistema } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useAlterarVinculo } from "@/api/consultas";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Botao } from "@/componentes/Botao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao } from "@/componentes/Confirmacao";
import { competencia, quantidade } from "@/formato";
import { consumoDoPar, resumoDosConvenios } from "./cedente";
import { AbaDeContrato } from "./AbaDeContrato";
import { ConveniosDoVinculo, TetoDoVinculo, type VinculoEditavel } from "./EdicaoDoVinculo";

/*
 * Só o que este detalhe lê. A aba Cedentes chega aqui pela lista da aplicação, e a
 * página do cedente, pela lista do cedente: `GET /sistemas/{id}/vinculos` tem teto de
 * 200 linhas, e procurar nela a clínica 201 diria que o vínculo não existe.
 */
type AplicacaoDoVinculo = Pick<Sistema, "id" | "nome"> & {
  cobranca: Pick<Sistema["cobranca"], "modalidade_cobranca">;
};
type CedenteDoVinculo = VinculoEditavel &
  Pick<CedenteDoSistema, "status" | "contrato" | "consumo">;

/*
 * O vínculo de um cedente com uma aplicação e, em `por_cedente`, o contrato do par — com
 * o MESMO componente da aba Contrato, no escopo do par. Mora no painel lateral, dos dois
 * lados: sobre a aba Cedentes da aplicação e sobre a aba Aplicações do cedente.
 *
 * Em `por_sistema` não há seção de contrato: o servidor recusa contrato de
 * cedente nessa modalidade (`422 modalidade_sem_contrato_de_cedente`), e
 * mostrar a seção vazia diria que falta algo que não pode existir.
 *
 * Nome, documento e situação já estão no título do painel: repeti-los aqui seria um
 * segundo título na mesma tela.
 */
export function DetalheDoCedente({
  sistema,
  cedente,
}: {
  sistema: AplicacaoDoVinculo;
  cedente: CedenteDoVinculo;
}) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const ehAdmin = pode("admin");
  const alterar = useAlterarVinculo(cliente, sistema.id, cedente.pessoa_id);

  const [modal, setModal] = useState<"convenios" | "teto" | null>(null);
  const [mudandoSituacao, setMudandoSituacao] = useState(false);

  const porCedente = sistema.cobranca.modalidade_cobranca === "por_cedente";
  const suspenso = cedente.status === "suspenso";

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2">
        <CaixaDeCampo rotulo="Convênios">
          {resumoDosConvenios(cedente.convenios_permitidos)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Teto próprio" dados>
          {cedente.limite_mensal_override === null
            ? null
            : quantidade(cedente.limite_mensal_override)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo={`Consumo ${competencia(cedente.consumo.competencia)}`} dados>
          {consumoDoPar(cedente.consumo)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Excedentes" dados>
          {quantidade(cedente.consumo.excedentes)}
        </CaixaDeCampo>
      </div>

      {ehAdmin && (
        <div className="flex flex-wrap gap-2">
          <Botao tom="secundario" onClick={() => setModal("convenios")}>
            Convênios
          </Botao>
          <Botao tom="secundario" onClick={() => setModal("teto")}>
            Teto próprio
          </Botao>
          <Botao
            tom={suspenso ? "secundario" : "destrutivo"}
            onClick={() => setMudandoSituacao(true)}
          >
            {suspenso ? (
              <Play aria-hidden="true" className="size-4" />
            ) : (
              <Pause aria-hidden="true" className="size-4" />
            )}
            {suspenso ? "Reativar" : "Suspender"}
          </Botao>
        </div>
      )}

      {porCedente && (
        <section className="flex flex-col gap-2">
          <h3 className="rotulo">Contrato</h3>
          <AbaDeContrato
            escopo={{ sistemaID: sistema.id, pessoaID: cedente.pessoa_id }}
            nome={cedente.razao_social}
            plano={
              cedente.contrato === null
                ? null
                : {
                    limite_mensal: cedente.contrato.limite_mensal,
                    politica: cedente.contrato.politica_excedente,
                  }
            }
            ativa
          />
        </section>
      )}

      {modal === "convenios" && (
        <ConveniosDoVinculo
          sistemaID={sistema.id}
          cedente={cedente}
          aoFechar={() => setModal(null)}
        />
      )}
      {modal === "teto" && (
        <TetoDoVinculo sistemaID={sistema.id} cedente={cedente} aoFechar={() => setModal(null)} />
      )}

      {/*
        Suspender para a emissão do cedente NESTA aplicação, na hora: é
        destrutiva. Reativar devolve o que já estava valendo: é leve. O
        contrato do par não muda em nenhuma das duas.
      */}
      <Confirmacao
        pedido={
          !mudandoSituacao
            ? null
            : suspenso
              ? {
                  severidade: "leve",
                  titulo: `Reativar ${cedente.razao_social}?`,
                  corpo: `Volta a aceitar emissão de ${sistema.nome}.`,
                  verbo: "Reativar vínculo",
                }
              : {
                  severidade: "destrutiva",
                  titulo: `Suspender ${cedente.razao_social}?`,
                  corpo: `${sistema.nome} para de emitir por este cedente.`,
                  verbo: "Suspender vínculo",
                }
        }
        aoCancelar={() => setMudandoSituacao(false)}
        aoConfirmar={async () => {
          await alterar.mutateAsync({
            tipo: "situacao",
            corpo: { status: suspenso ? "ativo" : "suspenso" },
          });
          setMudandoSituacao(false);
          avisos.feito(suspenso ? "Vínculo reativado" : "Vínculo suspenso");
        }}
      />
    </div>
  );
}
